// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { useEffectiveAuth } from '@/hooks/useEffectiveAuth';
import { resolveRleventsAccess } from '@/lib/rleventsAccess';
import { NO_ACCESS_LANDING_PATH } from '@/lib/rolePaths';

interface RoleAssignment {
  role: {
    slug: string;
    name: string;
    dashboard_path?: string;
  };
}

interface UserRoleData {
  roleAssignments: RoleAssignment[];
  allowedDashboardPaths: string[];
  isAdmin: boolean;
  hasMultipleRoles: boolean;
  defaultDashboardPath: string | null;
}

const EMPTY: UserRoleData = {
  roleAssignments: [],
  allowedDashboardPaths: [],
  isAdmin: false,
  hasMultipleRoles: false,
  defaultDashboardPath: null,
};

/**
 * RLevents only knows two kinds of signed-in user: organisation admins and
 * event staff. A lookup that FAILS is reported as `accessUnknown` — callers
 * must never treat it as "no access".
 */
export const useUserRoleAssignments = () => {
  const { user, profile } = useEffectiveAuth();
  const [roleData, setRoleData] = useState<UserRoleData>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [accessUnknown, setAccessUnknown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!user) {
        setRoleData(EMPTY);
        setAccessUnknown(false);
        setIsLoading(false);
        return;
      }

      // Permissions are decided by the account id alone (user_roles,
      // organization_members, event_staff). Waiting for the profile only put
      // one read behind another; this now runs alongside it.
      setIsLoading(true);
      setError(null);
      const access = await resolveRleventsAccess(user.id, { force: attempt > 0 });
      if (cancelled) return;

      if (access.status !== 'ok') {
        // We do not know. Hold the previous answer, surface a retry.
        setAccessUnknown(true);
        setError(access.error ?? 'Could not load your permissions.');
        setIsLoading(false);
        return;
      }

      setAccessUnknown(false);
      setRoleData({
        roleAssignments: access.roles.map((slug) => ({
          role: { slug, name: slug, dashboard_path: access.landingPath },
        })),
        allowedDashboardPaths: access.allowedPaths,
        isAdmin: access.isSuperAdmin,
        hasMultipleRoles: false,
        defaultDashboardPath: access.landingPath || NO_ACCESS_LANDING_PATH,
      });
      setIsLoading(false);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, attempt]);

  return {
    ...roleData,
    isLoading,
    accessUnknown,
    retry,
    error,
  };
};
